import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useContentRun, useSelectIdea } from "../hooks/useContent";
import { IdeaList } from "../components/ideas/IdeaList";
import { PipelineStatus } from "../components/pipeline/PipelineStatus";
import { Button } from "../components/ui/Button";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";

export function Ideas() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { data: runData, isLoading, error } = useContentRun(runId);
  const selectIdea = useSelectIdea(runId || "");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !runData?.data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          Failed to load content run.
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate("/home")}>
          Back to workspace
        </Button>
      </div>
    );
  }

  const run = runData.data;
  const agentStatus = run.agent_status;
  const ideas = run.ideas || [];

  // Determine if ideation is still processing
  const isIdeating =
    agentStatus.intake === "working" ||
    agentStatus.intake === "queued" ||
    agentStatus.ideation === "working" ||
    agentStatus.ideation === "queued";

  // Determine if any pipeline step has failed
  const hasFailed = Object.values(agentStatus).some((s) => s === "failed");

  // Check if pipeline is still processing (any step beyond ideation)
  const isProcessing = Object.values(agentStatus).some(
    (s) => s === "working" || s === "queued",
  );

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
  };

  const handleContinue = async () => {
    if (selectedIndex === null) return;

    try {
      await selectIdea.mutateAsync({ selected_idea_index: selectedIndex });
      navigate(`/review/${runId}`);
    } catch {
      // Error is available via selectIdea.error
    }
  };

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title="Choose an idea"
        description="Pick the content direction that best fits your goals. We'll create platform-specific content from your selection."
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Ideas" },
        ]}
        metadata={(
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
            Ideation / selection
          </span>
        )}
      />

      {(isProcessing || hasFailed) && (
        <PipelineStatus agentStatus={agentStatus} />
      )}

      <IdeaList
        ideas={ideas}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        loading={isIdeating && ideas.length === 0}
      />

      {ideas.length > 0 && (
        <div className="sticky bottom-20 rounded-[20px] border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur sm:bottom-4">
          <div className="flex flex-col items-end gap-2">
            <Button
              variant="primary"
              size="lg"
              disabled={selectedIndex === null}
              loading={selectIdea.isPending}
              onClick={handleContinue}
            >
              Continue with selected idea
            </Button>

            {selectIdea.isError && (
              <p className="text-sm text-red-600">
                Failed to select idea. Please try again.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
